import { useEffect, useState } from "react";
import { TIERS } from "@/data/pricing";

type Props = {
  names: Record<string, string>;
  fmt: (k: number) => string;
  weeksLabel: string;
};

const reduced = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Animated "code editor" that types an estimate call per project type and resolves the range. */
const EstimatorConsole = ({ names, fmt, weeksLabel }: Props) => {
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState(0);
  const [phase, setPhase] = useState<"type" | "run" | "done">("type");
  const [progress, setProgress] = useState(0);

  const tier = TIERS[idx];
  const call = `estimate("${tier.id}")`;

  useEffect(() => {
    if (reduced()) {
      setTyped(call.length);
      setPhase("done");
      setProgress(1);
      const t = setTimeout(() => setIdx((i) => (i + 1) % TIERS.length), 4000);
      return () => clearTimeout(t);
    }
    let t: ReturnType<typeof setTimeout>;
    if (phase === "type") {
      if (typed < call.length) t = setTimeout(() => setTyped((n) => n + 1), 45);
      else t = setTimeout(() => setPhase("run"), 250);
    } else if (phase === "run") {
      if (progress < 1) t = setTimeout(() => setProgress((p) => Math.min(1, p + 0.06)), 28);
      else setPhase("done");
    } else {
      t = setTimeout(() => {
        setIdx((i) => (i + 1) % TIERS.length);
        setTyped(0);
        setProgress(0);
        setPhase("type");
      }, 2600);
    }
    return () => clearTimeout(t);
  }, [phase, typed, progress, call.length]);

  const lo = Math.round(tier.price[0] * progress);
  const hi = Math.round(tier.price[1] * progress);

  return (
    <div className="estimator relative min-w-0" aria-hidden="true">
      <div className="estimator-glow" />
      <div className="relative overflow-hidden rounded-xl border border-border bg-card/80 backdrop-blur-sm">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-muted" />
          <span className="h-2.5 w-2.5 rounded-full bg-muted" />
          <span className="h-2.5 w-2.5 rounded-full bg-accent/70" />
          <span className="ml-3 font-mono text-[11px] text-muted-foreground">estimate.ts</span>
          <span className="ml-auto flex items-center gap-1.5 font-mono text-[10px] text-accent">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
            live
          </span>
        </div>

        <div className="overflow-x-auto p-5 font-mono text-[12.5px] leading-[1.85] sm:text-[13px]">
          <Line n={1}><K>import</K> {"{ scope, team }"} <K>from</K> <S>"@devcore/core"</S>;</Line>
          <Line n={2}>&nbsp;</Line>
          <Line n={3}><K>const</K> <F>estimate</F> = (type: <T>Project</T>) =&gt;</Line>
          <Line n={4}>&nbsp;&nbsp;scope(type).<F>flows</F>().<F>roles</F>().<F>integrations</F>()</Line>
          <Line n={5}>&nbsp;&nbsp;&nbsp;&nbsp;.<F>pipe</F>(team.<F>plan</F>);</Line>
          <Line n={6}>&nbsp;</Line>
          <Line n={7}>
            <span className="text-accent">❯</span> {call.slice(0, typed)}
            {phase === "type" && <span className="estimator-caret" />}
          </Line>
        </div>

        <div className="border-t border-border bg-background/60 px-5 py-4 font-mono text-[12px]">
          <div className="mb-2 flex items-center justify-between text-muted-foreground">
            <span>// {names[tier.id]}</span>
            <span>{Math.round(progress * 100)}%</span>
          </div>
          <div className="mb-3 h-[3px] overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-accent" style={{ width: `${progress * 100}%` }} />
          </div>
          <div className={`flex flex-wrap items-baseline gap-x-4 gap-y-1 transition-opacity duration-300 ${phase === "type" ? "opacity-30" : "opacity-100"}`}>
            <span className="font-heading text-xl font-bold text-foreground sm:text-2xl">
              {phase === "type" ? "— – —" : `${fmt(lo)} – ${fmt(hi)}`}
            </span>
            <span className="text-muted-foreground">
              {tier.weeks[0]}–{tier.weeks[1]} {weeksLabel}
            </span>
            {phase === "done" && <span className="text-accent">✓ resolved</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

const Line = ({ n, children }: { n: number; children: React.ReactNode }) => (
  <div className="flex gap-4 whitespace-nowrap">
    <span className="w-4 select-none text-right text-muted-foreground/50">{n}</span>
    <span className="text-foreground/90">{children}</span>
  </div>
);
const K = ({ children }: { children: React.ReactNode }) => <span className="text-accent/60">{children}</span>;
const F = ({ children }: { children: React.ReactNode }) => <span className="text-accent">{children}</span>;
const S = ({ children }: { children: React.ReactNode }) => <span className="text-muted-foreground">{children}</span>;
const T = ({ children }: { children: React.ReactNode }) => <span className="text-foreground">{children}</span>;

export default EstimatorConsole;
